import type Performer from "parog-web/models/performer";
import type Prescriptor from "parog-web/models/prescriptor";
import type {
  Execution,
  Section
} from "parog-web/utils/dmp-parsing/abstract-dmp-parser";
import AbstractDmpParser from "parog-web/utils/dmp-parsing/abstract-dmp-parser";
import {
  performerRetrieve,
  prescriptorRetrieve
} from "parog-web/utils/dmp-utils";
import { arrayIfNeeded } from "parog-web/utils/xml-parsed-helpers";

interface Prestation {
  sourceConcept: string;
  sourceValue: string;
  sourceLabel: string;
  quantity: number;
}

interface DrugsExecution extends Execution {
  prestations: Array<Prestation>;
  prescriptor: Prescriptor;
  performer: Performer;
}

interface DevicesExecution extends Execution {
  prestations: Array<Prestation>;
  prescriptor: Prescriptor;
  performer: Performer;
}

export type DrugsAndDevicesExecution = DrugsExecution | DevicesExecution;

export default class DrugsAndDevicesDmpParser extends AbstractDmpParser {
  journalEventClassName = "DrugsAndDevicesRefundedJournalEvent";

  canParse(section: Section): boolean {
    const code = section.code?._attributes?.code;
    const title = section.title?._text;
    return (
      (code == "10160-0" && title == "Médicaments") ||
      (code == "46264-8" && title == "Dispositifs médicaux")
    );
  }

  parse(section: Section): Array<DrugsAndDevicesExecution> {
    return this.getEntries(section)
      .map(entry => {
        let drugOrDeviceObject;

        const treatment = entry.substanceAdministration;
        const supply = entry.supply;

        if (treatment) {
          drugOrDeviceObject = this.parseTreatment(treatment);
        } else if (supply) {
          drugOrDeviceObject = this.parseSupply(supply);
        }

        if (drugOrDeviceObject) {
          return drugOrDeviceObject;
        }
      })
      .filter(Boolean) as DrugsAndDevicesExecution[];
  }

  private parseSupply(supply): DevicesExecution | undefined {
    const device = supply.participant?.participantRole?.playingDevice;

    if (
      device.code?._attributes?.nullFlavor ||
      device.code?._attributes?.codeSystem == "1.2.250.1.213.2.63"
    )
      return;

    const deviceCodeAttr = device?.code?._attributes
      ? device?.code?._attributes
      : device?.code?.translation?._attributes;
    const sourceConcept = "LPP";
    const sourceValue = deviceCodeAttr?.code;
    const sourceLabel = deviceCodeAttr?.displayName;

    const quantity = parseInt(supply.quantity?._attributes?.value);
    const occurredAt = this.parseOccurredAt(
      supply,
      "performer.time._attributes.value"
    );

    if (
      !sourceConcept ||
      !sourceValue ||
      !sourceLabel ||
      !quantity ||
      !occurredAt
    )
      return;

    return {
      occurredAt,
      prestations: [{ sourceConcept, sourceValue, sourceLabel, quantity }],
      prescriptor: prescriptorRetrieve(supply.author),
      performer: performerRetrieve(supply.performer)
    };
  }

  private parseTreatment(treatment): DrugsExecution | undefined {
    const drug =
      treatment.consumable?.manufacturedProduct?.manufacturedMaterial;

    if (
      drug.code?._attributes?.nullFlavor ||
      drug.code?._attributes?.codeSystem == "1.2.250.1.213.2.63"
    )
      return;

    let sourceConcept;
    let sourceValue;
    let sourceLabel;
    let quantity;

    const cisSource = drug.code?._attributes;
    const translations = arrayIfNeeded(drug.code?.translation);
    const cipSource = translations.find(
      translation =>
        translation._attributes?.codeSystem == "1.2.250.1.215.200.1.1.1"
    );
    const ucdSource = translations.find(
      translation =>
        translation._attributes?.codeSystem == "1.2.250.1.215.200.1.1.2"
    );

    if (cipSource) {
      sourceConcept = "CIP";
      sourceValue = cipSource._attributes?.code;
      sourceLabel = cipSource._attributes?.displayName;
    } else if (cisSource) {
      sourceConcept = cisSource.codeSystem;
      sourceValue = cisSource.code;
      sourceLabel = cisSource.displayName;
    } else if (ucdSource) {
      sourceConcept = "UCD";
      sourceValue = ucdSource._attributes?.code;
      sourceLabel = ucdSource._attributes?.displayName;
    }

    if (!sourceConcept || !sourceValue || !sourceLabel) return;

    let prescriptionEntryRelationship;
    if (Array.isArray(treatment.entryRelationship)) {
      prescriptionEntryRelationship = treatment.entryRelationship?.find(
        relationship => relationship._attributes?.typeCode == "REFR"
      );
    } else {
      if (treatment.entryRelationship?._attributes?.typeCode == "REFR") {
        prescriptionEntryRelationship = treatment.entryRelationship;
      }
    }

    let occurredAt;
    if (prescriptionEntryRelationship) {
      quantity = parseInt(
        prescriptionEntryRelationship.supply?.quantity?._attributes?.value
      );
      occurredAt = this.parseOccurredAt(
        prescriptionEntryRelationship,
        "supply.performer.time._attributes.value"
      );
    }

    if (!quantity || !occurredAt) return;

    let supply;
    let prescriptor;
    let performer;
    if (treatment.entryRelationship.length) {
      supply = treatment.entryRelationship.find(entry =>
        entry.hasOwnProperty("supply")
      );
    } else if (treatment.entryRelationship.hasOwnProperty("supply")) {
      supply = treatment.entryRelationship;
    }
    if (supply) {
      prescriptor = prescriptorRetrieve(supply.supply?.author);
      performer = performerRetrieve(supply.supply?.performer);
    }

    const prestations = [{ sourceConcept, sourceValue, sourceLabel, quantity }];

    const atcSource = translations.find(
      translation =>
        translation._attributes?.codeSystem == "1.2.250.1.215.200.1.2.2"
    );
    if (atcSource) {
      prestations[0]["atcCode"] = atcSource._attributes?.code;
      prestations[0]["atcLabel"] = atcSource._attributes?.displayName;
    }

    return {
      occurredAt,
      prestations,
      prescriptor,
      performer
    };
  }
}
